// src/app/services/llm-intent.service.ts
import { IIntegratedCare, ISingleOrder } from '@alberta/konexi-shared';
import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { AuditService } from '@services/audit/audit.service';
import { AuthService } from '@services/auth.service';
import { lastValueFrom, Subject } from 'rxjs';
import { PatientComponentModel } from 'src/app/business/patient/patients-component-model';
import { SingleOrderViewModel } from 'src/app/business/single-order/single-order-view-model';
import { SingleOrderedArticleLineViewModel } from 'src/app/business/single-order/single-ordered-articleline-view-model';
import { map } from 'src/app/common/mapper/mapper';
import { IntegratedCareDB } from 'src/app/common/repository/databases';
import { SingleOrderEditComponent } from 'src/app/patient/components/patient-detail/sections/integratedcare/single-order-edit/single-order-edit.component';
import { SwodocEditPage } from 'src/app/shared/components/document/document-edit/document-edit.modal';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { TenantConfigService } from 'src/app/shared/services/tenant-config/tenant-config.service';
import { TemplateComponentModel } from 'src/app/swodoc/_models/template-component-model';
import { IPrepareAuditMetaDataInterface } from 'src/app/swodoc/services/swodoc-create.service';
import { PatientViewModel } from '../business/patient/view-models/patient-view-model';

interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  birthday?: string;
  _id?: string;
}

interface IntegratedCare {
  _id: string;
  therapyName: string;
  therapyTypeName: string;
  iconName: string;
  color: string;
  locationName?: string;
}

export interface MessagePayer {
  _id: string;
  name: string;
  type: number;
  ikNumber?: string;
}

interface LlmIntentResponse {
  reply: string;
  actions: { name: string; args: any }[];
  patients?: Patient[];
  integratedCares?: IntegratedCare[];
  payers?: MessagePayer[];
  conversationHistory?: any[];
  auditData?: any; // Audit structure returned to LLM for further processing
}

export interface ThinkingStep {
  tool: string;
  label: string;
  status: 'running' | 'done';
}

@Injectable({ providedIn: 'root' })
export class LlmIntentService {
  // private readonly baseUrl = 'http://localhost:3000';
  private readonly baseUrl = 'https://ai.dev.k8s.api.halloalberta.de';
  private readonly router = inject(Router);

  readonly thinkingStep$ = new Subject<ThinkingStep>();
  private readonly ngZone = inject(NgZone);
  private readonly authService = inject(AuthService);
  private readonly auditService = inject(AuditService);
  private readonly templateComponentModel = inject(TemplateComponentModel);
  private readonly patientComponentModel = inject(PatientComponentModel);
  private readonly modalController = inject(ModalController);
  private readonly queryService = inject(QueryService);
  private readonly tenantConfigService = inject(TenantConfigService);

  // Store conversation history for the current session
  private conversationHistory: any[] = [];

  async processUtterance(
    text: string
  ): Promise<{ reply: string; patients?: Patient[]; integratedCares?: IntegratedCare[]; payers?: MessagePayer[] }> {
    const accessToken = await this.authService.getAccessToken();
    const res = await this._sendMessageSSE(
      text,
      {
        route: this.router.url,
        user: await this.authService.getAccount(),
      },
      this.conversationHistory
    );

    console.log('LLM reply:', res.reply);

    // Update conversation history with the response from server
    if (res.conversationHistory) {
      this.conversationHistory = res.conversationHistory;
    }

    // Execute actions and collect any results (e.g., audit data)
    let actionResults: any = {};
    for (const action of res.actions) {
      const result = await this.runAction(action);
      if (result) {
        actionResults[action.name] = result;
      }
    }

    // If actions produced results (like audit data), send them back to the LLM
    if (Object.keys(actionResults).length > 0) {
      return this.processUtterance(`[Action results: ${JSON.stringify(actionResults)}]`);
    }

    return {
      reply: res.reply,
      patients: res.patients,
      integratedCares: res.integratedCares,
      payers: res.payers,
    };
  }

  private async _sendMessageSSE(text: string, context: any, history: any[]): Promise<LlmIntentResponse> {
    const accessToken = await this.authService.getAccessToken();
    const response = await fetch(`${this.baseUrl}/llm-intents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ text, context, conversationHistory: history }),
    });

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        console.log('[SSE line]', JSON.stringify(line));
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          const data = JSON.parse(line.slice(6));
          console.log('[SSE event]', currentEvent, data);
          if (currentEvent === 'thinking') {
            this.ngZone.run(() => this.thinkingStep$.next(data as ThinkingStep));
          } else if (currentEvent === 'result') {
            return data as LlmIntentResponse;
          }
          currentEvent = '';
        }
      }
    }

    throw new Error('SSE stream ended without a result event');
  }

  /**
   * Clear the conversation history to start a fresh conversation
   */
  clearHistory() {
    this.conversationHistory = [];
  }

  /**
   * Get the current conversation history
   */
  getHistory() {
    return [...this.conversationHistory];
  }

  private async runAction(action: { name: string; args: any }): Promise<any> {
    switch (action.name) {
      case 'client_navigate_to_order': {
        const { patientId, integratedCareId, orderId } = action.args;
        await this.router.navigateByUrl('/', { skipLocationChange: true });
        this.router.navigate(['patient', patientId, 'integratedCare', integratedCareId, 'singleOrder', orderId], {
          queryParams: { openEdit: 'true' },
        });
        return null;
      }

      case 'client_open_order_wizard': {
        const { patientId, integratedCareId, orderData } = action.args;
        let integratedCare = await this.queryService.get<IIntegratedCare>(integratedCareId, IntegratedCareDB);
        if (!integratedCare) {
          integratedCare = await this.queryService.get<IIntegratedCare>(integratedCareId, IntegratedCareDB, {
            useOnlineDatabase: true,
          });
        }
        const userId = await this.authService.getCurrentUserId();

        const orderViewModel = new SingleOrderViewModel();
        if (orderData) {
          Object.assign(orderViewModel, map(orderData as ISingleOrder, new SingleOrderViewModel()));
          orderViewModel.orderedArticleLines = (orderViewModel.orderedArticleLines || []).map(rawLine => {
            const vm = new SingleOrderedArticleLineViewModel();
            return Object.assign(vm, rawLine);
          });
          await orderViewModel.initializeExisting(integratedCare, this.queryService, true);
          orderViewModel.deliveryDate = null;
          orderViewModel.setDeliveryTimeFrame();
        } else {
          await orderViewModel.initializeNew(integratedCare, this.queryService, this.tenantConfigService);
        }
        orderViewModel.createdBy = userId;

        const orderModal = await this.modalController.create({
          component: SingleOrderEditComponent,
          backdropDismiss: false,
          componentProps: {
            order: orderViewModel,
            integratedCare,
            isNew: true,
          },
          cssClass: 'fullPageModal',
        });
        await orderModal.present();
        const result = await orderModal.onDidDismiss();
        if (result?.data?.order) {
          const savedOrder = result.data.order as SingleOrderViewModel;
          await this.router.navigateByUrl('/', { skipLocationChange: true });
          this.router.navigate([
            'patient',
            patientId,
            'integratedCare',
            integratedCareId,
            'singleOrder',
            savedOrder._id,
          ]);
        }
        return null;
      }

      case 'client_open_patient_edit': {
        const { patientData } = action.args;
        const patientVM = new PatientViewModel();
        await patientVM.initializePatient(this.authService, this.queryService, this.tenantConfigService);
        if (patientData) {
          if (patientData.firstName) patientVM.firstName = patientData.firstName;
          if (patientData.lastName) patientVM.lastName = patientData.lastName;
          if (patientData.birthday) patientVM.birthday = new Date(patientData.birthday);
          if (patientData.payer) {
            patientVM.payer = patientData.payer;
          }
        }
        const { PatientEditComponent } = await import('../patient/components/patient-edit/patient-edit.component');
        const patientEditModal = await this.modalController.create({
          component: PatientEditComponent,
          backdropDismiss: false,
          componentProps: {
            item: patientVM,
          },
          cssClass: 'fullPageModal',
        });
        await patientEditModal.present();
        return null;
      }

      case 'client_open_patient':
        await this.router.navigateByUrl('/', { skipLocationChange: true });
        this.router.navigate(['patient', action.args.patientId]);
        return null;

      case 'client_open_caregiver':
        await this.router.navigateByUrl('/', { skipLocationChange: true });
        this.router.navigate(['patient', action.args.patientId, 'caregiver']);
        return null;

      case 'client_open_note':
        await this.router.navigateByUrl('/', { skipLocationChange: true });
        this.router.navigate(['patient', action.args.patientId, 'notes', action.args.noteId]);
        return null;

      case 'client_create_document':
        const { templateId, patientId, therapy, therapyType, woundLocation } = action.args;
        const patient = await lastValueFrom(this.patientComponentModel.get(action.args.patientId));
        const metaData: IPrepareAuditMetaDataInterface = {
          patient,
          selectedTherapy: therapy,
          selectedTherapyType: therapyType,
          selectedWoundLocation: woundLocation,
          additionalInfo: '',
        };
        const prefillData = await this.auditService.getPrefillData(
          templateId,
          patientId,
          metaData,
          await this.authService.getCurrentUserId()
        );

        const template = await lastValueFrom(this.templateComponentModel.get(action.args.templateId));

        const audit = await this.auditService.createPatientAudit(template, patientId, patient.regionId, prefillData);
        if (template.type > -1) {
          audit.type = template.type;
        }
        if (metaData.selectedTherapy || metaData.selectedTherapy === 0) {
          audit.therapyId = metaData.selectedTherapy;
        } else if (template.therapyId || template.therapyId === 0) {
          audit.therapyId = template.therapyId;
        }
        if (metaData.selectedTherapyType || metaData.selectedTherapyType === 0) {
          audit.therapyTypeId = metaData.selectedTherapyType;
        } else if (template.therapyTypeId || template.therapyTypeId === 0) {
          audit.therapyTypeId = template.therapyTypeId;
        }
        if (metaData.selectedWoundLocation) {
          audit.woundLocation = metaData.selectedWoundLocation.areaId.toString();
        }
        if (metaData.additionalInfo) {
          audit.additionalInfo = metaData.additionalInfo;
        }

        // Return audit structure to LLM for further processing (collecting additional inputs)
        return {
          audit,
        };

      case 'client_submit_document':
        // This action is called after LLM has collected additional inputs and filled the audit
        const { auditData } = action.args;
        const swodocEditModal = await this.modalController.create({
          component: SwodocEditPage,
          backdropDismiss: false,
          componentProps: {
            audit: auditData,
            isNew: true,
            doneIcon: 'alberta-fab-send-full',
            saveOnDismiss: false,
          },
          cssClass: 'fullPageModal',
        });

        await swodocEditModal.present();
        return null;

      default:
        console.warn('Unknown client action', action);
        return null;
    }
  }
}
